import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API, resolveImg } from '../lib/img';

const CONTENT_DEFAULTS = {
  hero_title: 'About the Authors',
  hero_subtitle: 'Two leaders who have dedicated their careers to understanding and enabling organisational transformation.',
  why_title: 'Why We Wrote This Book Together',
  why_paragraphs: []
};

const splitBioIntoParagraphs = (fullBio) =>
  (fullBio || '').split('. ').reduce((acc, sentence, idx, array) => {
    const sentenceWithPeriod = idx < array.length - 1 ? sentence + '.' : sentence;
    const currentParagraph = Math.floor(idx / 2);
    if (!acc[currentParagraph]) acc[currentParagraph] = [];
    acc[currentParagraph].push(sentenceWithPeriod);
    return acc;
  }, []);

const AboutAuthors = () => {
  const location = useLocation();
  const [authors, setAuthors] = useState([]);
  const [c, setC] = useState(CONTENT_DEFAULTS);

  useEffect(() => {
    fetch(`${API}/api/authors`)
      .then((r) => r.json())
      .then((d) => { if (d && d.authors) setAuthors(d.authors); })
      .catch(() => {});
    fetch(`${API}/api/content/about_authors`)
      .then((r) => r.json())
      .then((d) => { if (d && d.content) setC({ ...CONTENT_DEFAULTS, ...d.content }); })
      .catch(() => {});
  }, []);

  // Scroll to top, or to a specific author if a hash is present
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location, authors]);

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background: '#FFFFFF',
          padding: '120px 40px 80px'
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: '500',
              marginBottom: '24px',
              color: '#111111'
            }}
          >
            {c.hero_title}
          </h1>
          <p style={{ fontSize: '1.25rem', lineHeight: '1.8', color: '#333333' }}>
            {c.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Author Profiles */}
      {authors.map((author, index) => (
        <section
          key={author.id}
          id={`author-${author.id}`}
          className={index % 2 === 0 ? 'spectrum-wash' : ''}
          style={{
            backgroundColor: index % 2 === 0 ? undefined : '#FFFFFF',
            padding: '100px 40px'
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '60px',
              alignItems: 'center'
            }}
          >
            {/* Photo - alternates side */}
            <div style={{ order: index % 2 === 0 ? 1 : 2, display: 'flex', justifyContent: 'center' }}>
              <img
                src={resolveImg(author.photo_url)}
                alt={author.name}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: '500px',
                  objectFit: 'cover',
                  objectPosition: 'center 30%',
                  borderRadius: '8px',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                  filter: 'grayscale(100%)',
                  transition: 'filter 0.4s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.filter = 'grayscale(0%)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'grayscale(100%)'; }}
              />
            </div>

            {/* Bio */}
            <div style={{ order: index % 2 === 0 ? 2 : 1 }}>
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: '2.5rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: '#111111'
                }}
              >
                {author.name}
              </h2>
              <p
                style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#9A7B2E',
                  marginBottom: '24px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                {author.role}
              </p>
              <div>
                {splitBioIntoParagraphs(author.full_bio).map((sentences, pIndex) => (
                  <p
                    key={pIndex}
                    style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333333', marginBottom: '16px' }}
                  >
                    {sentences.join(' ')}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Why We Wrote This Book Together */}
      <section style={{ backgroundColor: '#111111', padding: '100px 40px', color: '#F4F1E8' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '2.5rem',
              fontWeight: '500',
              marginBottom: '32px',
              color: '#F4F1E8'
            }}
          >
            {c.why_title}
          </h2>
          <div>
            {(c.why_paragraphs || []).map((para, i) => (
              <p
                key={i}
                style={{ fontSize: '1.2rem', lineHeight: '1.9', color: '#F4F1E8', marginBottom: '20px' }}
              >
                {para}
              </p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutAuthors;
